import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { ApiTokenResponse } from '../types';
import apiTokenService from '../services/apiTokenService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageHeader, Badge, Spinner, ModalOverlay } from '../components/ui';

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString('de-DE') : '–';

const ApiTokens: React.FC = () => {
  usePageTitle('API Tokens');
  const [tokens, setTokens] = useState<ApiTokenResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  // Plaintext of the freshly created token – shown exactly once.
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiTokenResponse | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { loadTokens(); }, []);

  const loadTokens = async () => {
    try {
      setTokens(await apiTokenService.getAll());
    } catch (error) {
      toast.error('Tokens konnten nicht geladen werden');
      console.error('Failed to load API tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const created = await apiTokenService.create({ name: name.trim() });
      setNewSecret(created.plaintext);
      setShowCreate(false);
      setName('');
      await loadTokens();
    } catch (error: any) {
      toast.error(error.message || 'Token konnte nicht erstellt werden');
    } finally {
      setCreating(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    try {
      await apiTokenService.revoke(revoking.id);
      toast.success('Token widerrufen');
      setRevoking(null);
      await loadTokens();
    } catch (error: any) {
      toast.error(error.message || 'Token konnte nicht widerrufen werden');
    }
  };

  const copySecret = async () => {
    if (!newSecret) return;
    try {
      await navigator.clipboard.writeText(newSecret);
      toast.success('In die Zwischenablage kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-10 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <PageHeader
        title="API Tokens"
        subtitle="Schlüssel für den maschinellen Zugriff von Agenten auf die /agent-Schnittstelle"
        action={
          <button
            ref={triggerRef}
            onClick={() => setShowCreate(true)}
            className="btn-primary bg-brand-600 text-white px-5 py-2.5 rounded-lg hover:bg-brand-700 font-semibold text-sm"
          >
            + Neuer Token
          </button>
        }
      />

      {/* Create modal */}
      {showCreate && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="token-modal-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setShowCreate(false); }}
        >
          <div className="rounded-lg p-6 w-full max-w-md" style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="token-modal-title" className="text-lg font-semibold mb-4">Neuer API Token</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label htmlFor="token-name" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Name / Verwendungszweck
                </label>
                <input
                  id="token-name"
                  type="text"
                  required
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z. B. Build-Agent"
                  className="w-full p-2 border rounded-lg input-themed"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setName(''); }}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-60"
                >
                  {creating ? 'Erstelle…' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* One-time secret reveal */}
      {newSecret && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="secret-modal-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setNewSecret(null); }}
        >
          <div className="rounded-lg p-6 w-full max-w-md" style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="secret-modal-title" className="text-lg font-semibold mb-2">Token erstellt</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Kopiere den Token jetzt – er wird aus Sicherheitsgründen <strong>nur dieses eine Mal</strong> angezeigt.
            </p>
            <div
              className="flex items-center gap-2 p-3 rounded-lg mb-4 font-mono text-sm break-all"
              style={{ background: 'var(--table-header-bg)' }}
            >
              <span className="flex-1">{newSecret}</span>
              <button
                onClick={copySecret}
                className="px-3 py-1.5 rounded-md text-xs font-semibold shrink-0 bg-brand-600 text-white hover:bg-brand-700"
              >
                Kopieren
              </button>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Verwendung: HTTP-Header <code>X-API-Key: {newSecret.slice(0, 7)}…</code>
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setNewSecret(null)}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
              >
                Fertig
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Revoke confirmation */}
      {revoking && (
        <ModalOverlay
          role="alertdialog"
          ariaLabelledBy="revoke-dialog-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setRevoking(null); }}
        >
          <div className="rounded-lg p-6 w-full max-w-sm" style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="revoke-dialog-title" className="text-base font-semibold mb-2">Token widerrufen</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              <strong>{revoking.name}</strong> ({revoking.prefix}…) wird sofort ungültig. Agenten mit diesem Token verlieren den Zugriff.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRevoking(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
                autoFocus
              >
                Abbrechen
              </button>
              <button
                onClick={confirmRevoke}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
              >
                Widerrufen
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      <div className="rounded-xl shadow overflow-x-auto" style={{ background: 'var(--surface-bg)', boxShadow: 'var(--surface-shadow)', border: '1px solid var(--surface-border)' }}>
        <table className="min-w-full">
          <thead style={{ background: 'var(--table-header-bg)' }}>
            <tr>
              {['Name', 'Präfix', 'Status', 'Zuletzt genutzt', 'Erstellt'].map((h) => (
                <th key={h} className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--table-border)' }}>
                  {h}
                </th>
              ))}
              <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--table-border)' }}>
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} style={{ borderBottom: '1px solid var(--table-border)' }}>
                <td className="px-6 py-4 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t.name}</td>
                <td className="px-6 py-4 text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>{t.prefix}…</td>
                <td className="px-6 py-4">
                  <Badge variant={t.revokedAt ? 'red' : 'green'}>
                    {t.revokedAt ? 'Widerrufen' : 'Aktiv'}
                  </Badge>
                </td>
                <td className="px-6 py-4 text-sm" style={{ color: 'var(--text-muted)' }}>{fmtDate(t.lastUsedAt)}</td>
                <td className="px-6 py-4 text-sm" style={{ color: 'var(--text-muted)' }}>{fmtDate(t.createdAt)}</td>
                <td className="px-6 py-4 text-right text-sm font-medium">
                  {!t.revokedAt && (
                    <button
                      onClick={() => setRevoking(t)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 px-2 py-1.5 rounded"
                    >
                      Widerrufen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tokens.length === 0 && (
          <div className="text-center py-16 px-6">
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Noch keine Tokens</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Lege einen Token an, damit ein Agent ToDos über die <code>/agent</code>-Schnittstelle abrufen kann.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiTokens;
